import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { DataTableFilterField } from '@r/types/index.ts';
import pointsTranslation from '@r/translations/points-translation.ts';
import { PointPointCollectionReadModelTypeLabel } from '@r/api/generated/client.schemas.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';

export interface PointCollection {
  id?: number;
  amount: number;
  label?: string | null;
  status?: 'CORE' | 'IMPORTED' | 'SPENT' | 'EXPIRED' | 'CANCELED' | string;
  valueDate?: string | null;
  importedAt?: string | null;
  importedBy?: { fullname?: string | null; isDeleted?: boolean } | null;
  company?: { name?: string | null; uniqueCode?: string | null } | null;
  user?: { fullname?: string | null; isDeleted?: boolean } | null;
  companyName?: string;
  companyUniqueCode?: string;
  importedByFullname?: string;
  beneficiaryFullname?: string;
  createdAt?: string;
}

export const usePointsFilterFields = (): DataTableFilterField<PointCollection>[] => {
  const { t } = useTranslation();

  const statusEnum = Object.values(PointPointCollectionReadModelTypeLabel);
  const statusOptions = statusEnum.map((status) => ({
    label: status,
    value: status
  }));

  return [
    {
      label: t('label.point.company.name'),
      value: 'companyName',
      placeholder: t('label.point.company.name')
    },
    {
      label: t('label.point.unique.code'),
      value: 'companyUniqueCode',
      placeholder: t('label.point.unique.code')
    },
    {
      label: t('label.point.beneficiary'),
      value: 'beneficiaryFullname',
      placeholder: t('label.point.beneficiary')
    },
    {
      label: t('label.point.amount'),
      value: 'amount',
      placeholder: t('label.point.amount')
    },
    {
      label: t('label.point.pattern'),
      value: 'label',
      placeholder: t('label.point.pattern')
    },
    {
      label: t('label.point.status'),
      value: 'status',
      options: statusOptions
    },
    {
      label: t('label.point.value.date'),
      value: 'valueDate',
      placeholder: t('label.point.value.date.placeholder'),
      isDateRange: true
    },
    {
      label: t('label.point.import.date'),
      value: 'createdAt',
      placeholder: t('label.point.import.date.placeholder'),
      isDateRange: true
    },
    {
      label: t('label.point.import.user.fullname'),
      value: 'importedByFullname',
      placeholder: t('label.point.import.user.fullname')
    }
  ];
};

export const usePointsColumns = (): ColumnDef<PointCollection>[] => {
  const { t } = useTranslation();

  return [
    {
      id: 'companyName',
      accessorKey: 'company.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.point.company.name')} />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => row.original.company?.name ?? ''
    },
    {
      id: 'companyUniqueCode',
      accessorKey: 'company.uniqueCode',
      header: t('label.point.unique.code'),
      cell: ({ row }) => row.original.company?.uniqueCode ?? ''
    },
    {
      id: 'beneficiaryFullname',
      accessorKey: 'user',
      header: t('label.point.beneficiary'),
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.user?.fullname ?? ''}
          isDeleted={row.original.user?.isDeleted}
        />
      )
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.point.amount')} />
      ),
      enableHiding: false,
      cell: ({ row }) => pointsTranslation(t, row.original.amount, true)
    },
    {
      id: 'label',
      accessorKey: 'label',
      header: t('label.point.pattern'),
      enableSorting: false,
      cell: ({ row }) => row.original.label ?? ''
    },
    {
      accessorKey: 'status',
      header: t('label.point.status'),
      cell: ({ row }) => row.original.status ?? ''
    },
    {
      accessorKey: 'valueDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.point.value.date')} />
      ),
      enableSorting: true,
      enableHiding: false,
      cell: ({ row }) =>
        row.original.valueDate
          ? format(new Date(row.original.valueDate), 'dd/MM/yyyy', { locale: fr })
          : ''
    },
    {
      id: 'createdAt',
      accessorKey: 'importedAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.point.import.date')} />
      ),
      enableHiding: false,
      cell: ({ row }) =>
        row.original.importedAt
          ? format(new Date(row.original.importedAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    },
    {
      id: 'importedByFullname',
      accessorFn: (row) => row.importedBy?.fullname ?? '',
      header: t('label.point.import.user.fullname'),
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.importedBy?.fullname ?? ''}
          isDeleted={row.original.importedBy?.isDeleted}
        />
      )
    }
  ];
};
